import styled, { keyframes, css } from 'styled-components'

const popIn = keyframes`
  from { transform: scale(0.95) translateY(8px); opacity: 0; }
  to   { transform: scale(1) translateY(0); opacity: 1; }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`

// ── Widget flutuante ──────────────────────────────────────────────────────────

export const SidebarWrapper = styled.div<{ $open: boolean }>`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  width: 380px;
  height: min(560px, calc(100vh - 3rem));
  z-index: 1050;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
`

// ── Panel interno ─────────────────────────────────────────────────────────────

export const SidebarPanel = styled.div`
  width: 100%;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14);
  overflow: hidden;
  animation: ${popIn} 0.28s ease;
`

// ── Header do painel ──────────────────────────────────────────────────────────

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.2rem;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;

  h3 {
    font-size: 0.95rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f4f4f4;
    color: #333;
  }
`

// ── Área de mensagens ─────────────────────────────────────────────────────────

export const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 2px;
  }
`

// ── Mensagens ─────────────────────────────────────────────────────────────────

export const MessageRow = styled.div<{ $role: 'user' | 'assistant' }>`
  display: flex;
  justify-content: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  animation: ${fadeIn} 0.2s ease;
`

export const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 88%;
  padding: 0.65rem 0.9rem;
  border-radius: ${({ $role }) =>
    $role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px'};
  background: ${({ $role }) => ($role === 'user' ? '#0090D3' : '#f4f6f8')};
  color: ${({ $role }) => ($role === 'user' ? '#fff' : '#1a1a1a')};
  font-size: 0.84rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;

  strong {
    font-weight: 600;
  }

  code {
    font-family: 'Courier New', monospace;
    font-size: 0.8rem;
    background: ${({ $role }) =>
      $role === 'user' ? 'rgba(255,255,255,0.2)' : '#e8edf2'};
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
  }
`

export const StreamingCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 0.85em;
  background: #555;
  margin-left: 2px;
  border-radius: 1px;
  vertical-align: middle;
  animation: ${blink} 0.8s step-end infinite;
`

// ── Tool cards ────────────────────────────────────────────────────────────────

export const ToolCard = styled.div<{ $status: 'running' | 'done' }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${({ $status }) => ($status === 'running' ? '#fffbe6' : '#f0faf4')};
  border: 1px solid ${({ $status }) => ($status === 'running' ? '#ffe58f' : '#b7eb8f')};
  border-radius: 8px;
  font-size: 0.78rem;
  color: #444;
  animation: ${fadeIn} 0.2s ease;
  max-width: 88%;

  .tool-icon {
    flex-shrink: 0;
    margin-top: 1px;
  }

  .tool-body {
    flex: 1;
    min-width: 0;
  }

  .tool-label {
    font-weight: 500;
    color: ${({ $status }) => ($status === 'running' ? '#874d00' : '#135200')};
  }

  .tool-result {
    margin-top: 0.3rem;
    color: #555;
    white-space: pre-wrap;
    font-size: 0.75rem;
    max-height: 100px;
    overflow-y: auto;
  }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`

export const Spinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #e0e0e0;
  border-top-color: #0090D3;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`

// ── Empty state ───────────────────────────────────────────────────────────────

export const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #aaa;
  text-align: center;
  padding: 2rem;
  user-select: none;

  svg {
    opacity: 0.4;
  }

  p {
    font-size: 0.83rem;
    line-height: 1.5;
    color: #bbb;
    margin: 0;
  }

  .suggestions {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-top: 0.5rem;
    width: 100%;
  }
`

export const SuggestionChip = styled.button`
  background: #f4f6f8;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.78rem;
  color: #555;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #e8f4fd;
    border-color: #0090D3;
    color: #0090D3;
  }
`

// ── Input area ────────────────────────────────────────────────────────────────

export const InputArea = styled.div`
  padding: 0.85rem 1rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  background: #fff;
  flex-shrink: 0;
`

export const TextInput = styled.textarea`
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 0.6rem 0.8rem;
  font-size: 0.84rem;
  font-family: inherit;
  resize: none;
  min-height: 40px;
  max-height: 120px;
  line-height: 1.4;
  color: #1a1a1a;
  transition: border-color 0.15s;
  overflow-y: auto;

  &:focus {
    outline: none;
    border-color: #0090D3;
  }

  &::placeholder {
    color: #bbb;
  }

  &:disabled {
    background: #fafafa;
    cursor: not-allowed;
  }
`

export const SendButton = styled.button<{ $active: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${({ $active }) => ($active ? '#0090D3' : '#e0e0e0')};
  color: ${({ $active }) => ($active ? '#fff' : '#aaa')};
  cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.1s;

  &:hover {
    ${({ $active }) =>
      $active &&
      css`
        background: #007ab8;
        transform: scale(1.05);
      `}
  }

  &:active {
    ${({ $active }) =>
      $active &&
      css`
        transform: scale(0.96);
      `}
  }
`

